/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    ALL_INSTITUTIONS_CODE,
    IInstitution
} from '@ngt/opms';

import { styled, Typography } from '@mui/material';

/*
 * Used to format datetimes
 */

import { GridColDef } from '@mui/x-data-grid';
import type { } from '@mui/x-data-grid/themeAugmentation';



import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ImageDocument, MriImageUpload, Patient } from '../../api/dtos';
import MriImageUploadsContext from '../../hooks/context/MriImageUploadsContext';
import ImageCell from './ImageCell';


/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

const StyledTyopography = styled(Typography)(() => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',

}));

const useImageUploadColumns = (
    institutions: IInstitution[] | null, 
    canAdministerOpms: boolean,
    isSiteUser: boolean,
    isCentralReviewer: boolean
): GridColDef<Patient>[] => {
    
    const { data: mriImageUploads } = useContext(MriImageUploadsContext);

    

    const { institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
            return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
        }, [institutionCode]);
    
    const columns = React.useMemo(() => {

        const c: (GridColDef<MriImageUpload> | boolean)[] = [
            {
                field: 'participantNumber',
                headerName: 'Participant Number',
                width: canAdministerOpms ? 165 : 265
            },
            canAdministerOpms && {
                field: 'initials',
                headerName: 'Initials',
                //width: 200
            },
            !parsedInstitutionCode && {
                field: 'institutionName',
                headerName: 'Site Name',
                width: 340
            },
            {
                field: 'imageDocument',
                headerName: 'MRI',
                filterable: false,
                sortable: false,
                renderCell: rowData => {

                    const imageDocument: ImageDocument | undefined = useMemo(() => {
                        return mriImageUploads?.find((x: MriImageUpload) => x.imageDocument?.id === rowData.row.imageDocument?.id)?.imageDocument;
                    }, [rowData.row.imageDocument, mriImageUploads]);

                    return <ImageCell 
                        canAdministerOpms={canAdministerOpms}
                        isSiteUSer={isSiteUser}
                        isCentralReviewer={isCentralReviewer}
                        initialImageDocument={imageDocument} 
                        uploadId={rowData.row.id} 
                        displayImage={true}
                        displayCem={false}
                    />;
                },
                width: !parsedInstitutionCode ? 248.5 : 418.5
            },
            {
                field: 'cEMDocument',
                headerName: 'CEM',
                filterable: false,
                sortable: false,
                renderCell: rowData => {

                    const cemDocument: ImageDocument | undefined = useMemo(() => {
                        return mriImageUploads?.find((x: MriImageUpload) => x.cemDocument?.id === rowData.row.cemDocument?.id)?.cemDocument;
                    }, [rowData.row.cemDocument, mriImageUploads]);

                    return <>
                            
                        {
                            (!cemDocument && !rowData.row.mammogramPerformed) ? (
                                <StyledTyopography variant="body2">
                                    N/A
                                </StyledTyopography>
                            ) : (
                                <ImageCell 
                                    canAdministerOpms={canAdministerOpms}
                                    isSiteUSer={isSiteUser}
                                    isCentralReviewer={isCentralReviewer}
                                    initialImageDocument={cemDocument} 
                                    uploadId={rowData.row.id}
                                    displayImage={false}
                                    displayCem={true}
                                />
                            )
                        }
                        
                    </>;
                },
                width: !parsedInstitutionCode ? 248.5 : 418.5,
                
            }
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [institutions, canAdministerOpms, isSiteUser, mriImageUploads]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useImageUploadColumns;